import { useState } from "react";
import {
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetError,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";
import { toast } from "react-toastify";

import {
  CODE_VERIFICATION_CHANNELS,
  CODE_VERIFICATION_SCOPES,
  COUNTRY_CODES,
  DEFAULT_CODE_LENGTH,
  INITIAL_VERIFICATION_SESSION,
} from "~/common/constants/verificationCode";
import { customErrorSchema } from "~/common/types";
import { CodeVerificationSession } from "~/common/types/verificationCode";
import {
  useCancelVerificationCode,
  useFormatHook,
  useRetryVerificationCode,
  useSendVerificationCode,
  useVerifyCode,
} from "~/patients/hooks";
import { useOnBoardingNewPatientStore } from "~/patients/stores/useOnBoardingNewPatientStore";
import {
  NewPatientSchemaType,
  SignUpPatientSchemaType,
} from "~/patients/utils";
import { useSubmitNewPatientSignUp } from "./useSubmitNewPatientSignUp";

interface useSignUpNewPatientProps {
  clearErrors: UseFormClearErrors<SignUpPatientSchemaType>;
  getValues: UseFormGetValues<SignUpPatientSchemaType>;
  setError: UseFormSetError<SignUpPatientSchemaType>;
  setValue: UseFormSetValue<SignUpPatientSchemaType>;
  trigger: UseFormTrigger<SignUpPatientSchemaType>;
  providerId?: string;
  organizationId?: string;
  invitationCode?: string;
}
export const useSignUpNewPatient = ({
  clearErrors,
  getValues,
  setError,
  setValue,
  trigger,
  providerId,
  organizationId,
  invitationCode,
}: useSignUpNewPatientProps) => {
  const { nextStep, previousStep, skipStep, newPatient, setNewPatient } =
    useOnBoardingNewPatientStore();
  const { stripPhoneNumber } = useFormatHook();
  const [verificationSession, setVerificationSession] =
    useState<CodeVerificationSession>(INITIAL_VERIFICATION_SESSION);

  const { mutate: sendVerificationCode, isPending: isSendingCode } =
    useSendVerificationCode();
  const { mutate: verifyCode, isPending: isVerifyingCode } = useVerifyCode();
  const { mutate: retryVerificationCode, isPending: isRetryingCode } =
    useRetryVerificationCode();
  const { mutate: cancelVerificationCode, isPending: isCancellingCode } =
    useCancelVerificationCode();

  const values = getValues() as NewPatientSchemaType;

  const { onSubmitSignUp, isPending: isCreatingPatient } =
    useSubmitNewPatientSignUp({
      values,
      trigger,
      providerId,
      organizationId,
      invitationCode,
    });

  const isActionPending = [
    isCreatingPatient,
    isSendingCode,
    isVerifyingCode,
    isRetryingCode,
    isCancellingCode,
  ].some(Boolean);

  const onPhoneNumberStepCompleted = () => {
    const submittedPhoneNumber = getValues("phone_number.phone_number");

    setNewPatient({
      ...newPatient,
      phone_number: { phone_number: submittedPhoneNumber },
    });

    sendVerificationCode(
      {
        phone_number: {
          number: stripPhoneNumber(submittedPhoneNumber),
          country_code: COUNTRY_CODES.US,
        },
        channel: CODE_VERIFICATION_CHANNELS.SMS,
        scope: CODE_VERIFICATION_SCOPES.SIGNUP,
      },
      {
        onSuccess: ({ verification_token, can_resend_at }) => {
          setVerificationSession((prev) => ({
            ...prev,
            canResendAt: new Date(can_resend_at).getTime(),
            token: verification_token,
            isExpired: false,
          }));
          nextStep();
        },
      },
    );
  };

  const onCodeVerificationStepCompleted = () => {
    if (!verificationSession.token) {
      setError("verification.code", {
        message: "Verification session expired. Please request a new code.",
      });
      return;
    }

    const submittedCode = getValues("verification.code");

    if (submittedCode.length < DEFAULT_CODE_LENGTH) {
      setError("verification.code", {
        message: `${DEFAULT_CODE_LENGTH} character code is required`,
      });
      return;
    }

    verifyCode(
      {
        verification_code: submittedCode,
        verification_token: verificationSession.token,
      },
      {
        onSuccess: ({ one_use_token }) => {
          setVerificationSession(INITIAL_VERIFICATION_SESSION);
          setNewPatient({
            ...newPatient,
            one_use_token,
          });
          nextStep();
        },
        onError: (error) => {
          const result = customErrorSchema.safeParse(error);
          if (result.success) {
            if (result.data.response.data.detail === "Token expired") {
              setError("verification.code", {
                message: "Code has expired, please try another one.",
              });
              setVerificationSession((prev) => ({
                ...prev,
                isExpired: true,
              }));
              return;
            }
          }
          setError("verification.code", {
            message: "This code doesn't match. Please try again.",
          });
        },
      },
    );
  };

  const onRetry = () => {
    const currentPhone = newPatient?.phone_number?.phone_number;

    if (!currentPhone) {
      setError("verification.code", {
        message: "No phone was provided",
      });
      return;
    }

    if (!verificationSession.token) {
      setError("verification.code", {
        message: "No verification token present.",
      });
      return;
    }

    const restartVerificationFlow = () => {
      if (!verificationSession.token) return;

      cancelVerificationCode(
        {
          verification_token: verificationSession.token,
        },
        {
          onSuccess: () => {
            sendVerificationCode(
              {
                phone_number: {
                  number: stripPhoneNumber(currentPhone),
                  country_code: COUNTRY_CODES.US,
                },
                scope: CODE_VERIFICATION_SCOPES.SIGNUP,
                channel: CODE_VERIFICATION_CHANNELS.SMS,
              },
              {
                onSuccess: ({ verification_token, can_resend_at }) => {
                  setVerificationSession((prev) => ({
                    ...prev,
                    token: verification_token,
                    canResendAt: new Date(can_resend_at).getTime(),
                    isExpired: false,
                  }));
                },
              },
            );
          },
        },
      );
    };

    if (verificationSession.isExpired) {
      restartVerificationFlow();
      return;
    }

    retryVerificationCode(
      { verification_token: verificationSession.token },
      {
        onSuccess: ({ can_resend_at, verification_token }) => {
          setVerificationSession((prev) => ({
            ...prev,
            canResendAt: new Date(can_resend_at).getTime(),
            token: verification_token,
            isExpired: false,
          }));
        },
        onError: (error) => {
          const result = customErrorSchema.safeParse(error);
          if (result.success) {
            if (result.data.response.data.detail === "Token expired") {
              restartVerificationFlow();
              return;
            }
            toast.error(result.data.response.data.detail);
          }
        },
      },
    );
  };

  const onBack = (schemaName: keyof NewPatientSchemaType) => {
    if (verificationSession.token) {
      cancelVerificationCode(
        { verification_token: verificationSession.token },
        {
          onSuccess: () => {
            setVerificationSession(INITIAL_VERIFICATION_SESSION);
          },
        },
      );
    }
    if (schemaName === "password") {
      setValue("verification.code", "");
      skipStep(-2);
      return;
    }
    if (schemaName === "verification") {
      clearErrors("verification.code");
      previousStep();
      return;
    }
    previousStep();
  };

  const onNext = async (schemaName: keyof NewPatientSchemaType) => {
    const isValid = await trigger(schemaName);
    if (isValid) {
      const newSignUpStepToAction: Record<string, () => void> = {
        phone_number: onPhoneNumberStepCompleted,
        verification: onCodeVerificationStepCompleted,
      };

      const runAction = newSignUpStepToAction[schemaName];

      if (runAction) {
        return runAction();
      }

      nextStep();
    }
  };

  return {
    onNext,
    onRetry,
    onBack,
    isActionPending,
    onSubmitSignUp,
    canResendAt: verificationSession.canResendAt ?? 0,
  };
};
